import type { Ref } from 'react';
import { Checkbox } from '../Checkbox/Checkbox';
import styles from './RowSelection.module.css';

export interface SelectionHeaderCellProps {
    /** The box's hidden label, sentence case: `Select every account on this page`. */
    readonly label: string;
    /** The selection over the rows on screen — `selection.page`: unchecked, mixed or checked. */
    readonly state: 'none' | 'some' | 'all';
    /** `selection.togglePage`: selects the current page, or clears it when it is all selected. */
    readonly onToggle: () => void;
    /** Rendered as `aria-disabled`, still focusable (e.g. no rows yet); the reason by `aria-describedby`. */
    readonly disabled?: boolean;
    readonly 'aria-describedby'?: string;
    /**
     * The input — the `BulkActionBar`'s `focusAfterClear` target. `| undefined` for
     * `exactOptionalPropertyTypes`, as `Button` types its ref.
     */
    readonly ref?: Ref<HTMLInputElement> | undefined;
    /** The test hook, forwarded to the input. */
    readonly 'data-testid'?: string;
}

export interface SelectionCellProps {
    /** The box's hidden label, naming the row: `Select Ceylon Leaf Ltd`. */
    readonly label: string;
    /** `selection.isSelected(id)`. */
    readonly selected: boolean;
    /** `() => selection.toggleRow(id)`. */
    readonly onToggle: () => void;
    readonly disabled?: boolean;
    readonly 'aria-describedby'?: string;
    /** The test hook, forwarded to the input. */
    readonly 'data-testid'?: string;
}

/**
 * Design-system §6's selection column (*Data table (F32) → Selection*): **the checkbox column pinned
 * first**, the header box over the current page — unchecked, mixed or checked — and one box per row.
 * Built in `src/ui` by F5-h's offboarding lists (F5-h WI-1.3), for F32-c's and F32-b's lists after it.
 *
 * Table parts with display props, not a table (the `SortableHeaderCell` arrangement, F5-h WI-1.3 plan
 * Decision 1): `SelectionHeaderCell` renders a `<th scope="col">` for the first cell of a `<thead>` row, and
 * `SelectionCell` a `<td>` for the first cell of each body row; a skeleton row puts an empty
 * `<td aria-hidden="true">` there. The state comes from `useRowSelection`.
 *
 * Each holds a `Checkbox` whose label is visually hidden — the row's own words sit beside it — and
 * kept as its name; the label fills the cell, so the whole cell toggles the box. A selected row's cell
 * carries `data-selected="true"` and draws §5's selected edge; `aria-selected` is not used, because a
 * plain table's row may not carry it.
 *
 * They hold no authorization (F5-h WI-1.3 plan Decision 15): a consumer that may not act passes `disabled`
 * and a reason.
 */
export function SelectionHeaderCell({
    label,
    state,
    onToggle,
    disabled = false,
    'aria-describedby': describedBy,
    ref,
    'data-testid': testId,
}: SelectionHeaderCellProps) {
    return (
        <th className={styles.headerCell} scope="col">
            <Checkbox
                aria-describedby={describedBy}
                checked={state === 'all'}
                disabled={disabled}
                indeterminate={state === 'some'}
                label={label}
                labelHidden
                onChange={onToggle}
                ref={ref}
                /* testid-exempt: a component, not an element; the input it renders is named by the required label and carries the caller's data-testid when given */
                {...(testId === undefined ? {} : { 'data-testid': testId })}
            />
        </th>
    );
}

/** A body row's selection box — see `SelectionHeaderCell`. */
export function SelectionCell({
    label,
    selected,
    onToggle,
    disabled = false,
    'aria-describedby': describedBy,
    'data-testid': testId,
}: SelectionCellProps) {
    return (
        <td className={styles.cell} data-selected={selected ? 'true' : 'false'}>
            <Checkbox
                aria-describedby={describedBy}
                checked={selected}
                disabled={disabled}
                label={label}
                labelHidden
                onChange={onToggle}
                /* testid-exempt: a component, not an element; the input it renders is named by the required label and carries the caller's data-testid when given */
                {...(testId === undefined ? {} : { 'data-testid': testId })}
            />
        </td>
    );
}
